import React, { FC } from 'react';
import { gqlTalentProfileUnion, gqlPublicCredit } from '@minecraft.graphql-types';
import { useTranslation } from '@blocs.i18n';
import { PicksHelperText, PickButtonReps } from '@blocs.package-presentation';
import { MediaType } from '../SummaryTypes';
import {
  SummaryContainer,
  SummaryPrimaryPhotoWrapper,
  SummaryInfo,
  SummaryInfoItem,
  SummaryInfoItemInner,
  SummaryPickWrapper,
  SummaryPickHelperTextWrapper,
  PicksWrapperWrapper,
} from '../SummaryStyles';
import SummaryPrimaryPhoto from '../SummaryPrimaryPhoto';
import SummaryProfileInfo from '../SummaryProfileInfo';
import SummaryCommon from '../SummaryCommon';
import SummaryPrimaryWorkingLocation from '../SummaryPrimaryWorkingLocation';
import SummaryMeasurements from '../SummaryMeasurements';
import SummaryCredits from '../SummaryCredits';
import SummaryReels from '../SummaryReels';

type Props = {
  showPhoto: boolean;
  stageName: string;
  submissionNote?: string;
  overscaleNote?: string;
  primaryPhoto: MediaType;
  talentUnions: ReadonlyArray<gqlTalentProfileUnion>;
  blurb: string;
  primaryWorkingLocation: string;
  height: string;
  weight: string;
  credits: ReadonlyArray<gqlPublicCredit>;
  reels: ReadonlyArray<MediaType>;
  userName?: string;
  userNameModalToggle?: () => void;
  presentationGuid?: string;
  presentationId?: number;
  pickedBy?: { id: string; useName: number }[];
  pickCount?: number;
  profileId?: string;
  showPicks?: boolean;
};

const SummaryDefaultView: FC<Props> = ({
  showPhoto,
  stageName,
  primaryPhoto,
  talentUnions,
  blurb,
  primaryWorkingLocation,
  height,
  weight,
  credits,
  reels,
  userName,
  userNameModalToggle,
  presentationGuid,
  presentationId,
  pickedBy,
  pickCount,
  profileId,
  showPicks,
  submissionNote,
  overscaleNote,
}) => {
  const { t } = useTranslation();

  return (
    <SummaryContainer>
      <SummaryPrimaryPhotoWrapper>
        {showPhoto && <SummaryPrimaryPhoto primaryPhoto={primaryPhoto} />}
      </SummaryPrimaryPhotoWrapper>
      <SummaryInfo>
        <SummaryInfoItem>
          <SummaryProfileInfo stageName={stageName} talentUnions={talentUnions} />
        </SummaryInfoItem>
        {submissionNote && (
          <SummaryInfoItem>
            <SummaryCommon text={submissionNote} title={t('common:profileEdit.submissionNote')} />
          </SummaryInfoItem>
        )}
        {submissionNote && overscaleNote && <hr />}
        {overscaleNote && (
          <SummaryInfoItem>
            <SummaryCommon text={overscaleNote} title={t('common:profile.overscaleNote')} />
          </SummaryInfoItem>
        )}
        {showPicks && (
          <SummaryPickWrapper>
            <SummaryPickHelperTextWrapper>
              <PicksHelperText
                userName={userName}
                handleUserNameModalToggle={userNameModalToggle}
                helpText={t('common:picksHelperText.helperText')}
                helpTextWithName={t('common:picksHelperText.helperTextWithName')}
              />
            </SummaryPickHelperTextWrapper>
            <PicksWrapperWrapper>
              <PickButtonReps
                isPublic
                entityId={profileId}
                userName={userName}
                presentationGuid={presentationGuid}
                presentationId={presentationId}
                fullWidth={false}
                pickedBy={pickedBy}
                pickCount={pickCount}
                handleUserNameModalToggle={userNameModalToggle}
              />
            </PicksWrapperWrapper>
          </SummaryPickWrapper>
        )}
        <SummaryInfoItem>
          <SummaryCommon text={blurb} title={t('common:form.title.about')} />
        </SummaryInfoItem>
        <SummaryInfoItem>
          <SummaryInfoItemInner>
            <SummaryPrimaryWorkingLocation primaryWorkingLocation={primaryWorkingLocation} />
            <SummaryMeasurements height={height} weight={weight} />
          </SummaryInfoItemInner>
        </SummaryInfoItem>
        <SummaryInfoItem>
          <SummaryCredits creditList={credits} />
        </SummaryInfoItem>
        <SummaryInfoItem>
          <SummaryReels reels={reels} />
        </SummaryInfoItem>
      </SummaryInfo>
    </SummaryContainer>
  );
};

export default SummaryDefaultView;
